import React from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";

interface ExportLoadingBackdropProps {
  open: boolean;
  message?: string;
  progress?: number | null;
  subMessage?: string;
}

export const ExportLoadingBackdrop: React.FC<ExportLoadingBackdropProps> = ({
  open,
  message = "Processing records. Please wait...",
  progress = null,
  subMessage = "Please wait while all records are processed in the background. Do not close this tab.",
}) => {
  const hasProgress = typeof progress === "number" && !Number.isNaN(progress);
  const clampedProgress = hasProgress
    ? Math.max(0, Math.min(100, Math.round(progress)))
    : 0;

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 9999,
        color: "#FFFFFF",
        backgroundColor: "rgba(15, 23, 42, 0.78)",
        backdropFilter: "blur(5px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 440,
          backgroundColor: "#FFFFFF",
          color: "#0F172A",
          borderRadius: "6px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.25)",
          p: 3.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 1.75,
        }}
      >
        <Box sx={{ position: "relative", display: "inline-flex", mb: 0.5 }}>
          <CircularProgress
            variant={hasProgress && clampedProgress > 0 ? "determinate" : "indeterminate"}
            value={hasProgress && clampedProgress > 0 ? clampedProgress : undefined}
            size={56}
            thickness={4.2}
            sx={{ color: "#0072BC" }}
          />
          {hasProgress && (
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: 800,
                  color: "#0072BC",
                }}
              >
                {`${clampedProgress}%`}
              </Typography>
            </Box>
          )}
        </Box>

        <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#0F172A" }}>
          Generating Export...
        </Typography>

        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: 600,
            color: "#334155",
            minHeight: 20,
          }}
        >
          {message}
        </Typography>

        <Box sx={{ width: "100%", mt: 0.5 }}>
          <LinearProgress
            variant={hasProgress ? "determinate" : "indeterminate"}
            value={hasProgress ? clampedProgress : undefined}
            sx={{
              height: 8,
              borderRadius: "4px",
              backgroundColor: "#E2E8F0",
              "& .MuiLinearProgress-bar": {
                borderRadius: "4px",
                backgroundColor: "#0072BC",
                transition: "transform 0.2s linear",
              },
            }}
          />
          {hasProgress && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 0.75,
              }}
            >
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#64748B" }}>
                Background Processing
              </Typography>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#0072BC" }}>
                {clampedProgress}% Complete
              </Typography>
            </Box>
          )}
        </Box>

        {subMessage && (
          <Typography
            sx={{
              fontSize: 12,
              color: "#64748B",
              mt: 0.5,
              lineHeight: 1.45,
            }}
          >
            {subMessage}
          </Typography>
        )}
      </Box>
    </Backdrop>
  );
};

